import { useQuery, useMutation } from 'react-query';
import { axiosRequest } from '../utils/axiosRequest';
import { useState, useCallback, useEffect } from 'react';
import { ISmsSendStatus, IMediaStatus } from '../global/interfaces';
import toggleToast from '../utils/messaging';

const useSession = (bookingId: string | undefined = '') => {
  const axios = axiosRequest();

  const fetchSessionDetails = async (bookingId: string | null) => {
    console.log(`fetchSessionDetails /sessions/manage_report/${bookingId}`);
    const { data } = await axios.get(`/sessions/manage_report/${bookingId}`);

    console.log('do fetchSessionDetails...');

    if (data.status === 'success') {
      return data.data;
    }

    throw new Error('Failed to booking details for selected booking');
  };

  const {
    data: sessionData,
    isLoading: isFetchingSessionDetails,
    refetch: refetchSessionDetails,
    error: errorFetchingSessionDetails,
  } = useQuery(
    ['manage-report', bookingId],
    () => fetchSessionDetails(bookingId),
    {
      retry: 0,
      enabled: false,
    },
  );

  const downloadSessionData = async ({
    bookingId,
    object = 'transcript',
    raw = false,
  }: {
    bookingId: string;
    object?: string;
    raw?: boolean;
  }) => {
    console.log(
      `downloadSessionData for booking_id: ${bookingId}, raw: ${raw}, objects: ${object}`,
    );
    try {
      const response = await axios.post(
        `/sessions/download_session_data/${bookingId}`,
        { object, raw },
        object === 'all' ? { responseType: 'blob' } : {},
      );

      console.log('downloadSessionData data:', response);

      if (
        response.status === 200 &&
        response.headers['content-type'] === 'application/zip'
      ) {
        return response.data;
      } else if (response?.data?.status === 'success') {
        return response.data;
      }

      throw new Error('Failed fetchBookingDetails for selected booking');
    } catch (error) {
      console.log('downloadSessionData error:', error);
      toggleToast('Error downloading session data.', 'error');
    }
  };

  const fetchInspectorRoomToken = useCallback(
    async (booking_session_code: string) => {
      console.log(`fetchInspectorRoomToken: ${booking_session_code}`);
      if (!booking_session_code)
        throw new Error('Error: missing booking_session_code.');

      const { data } = await axios.get(
        `sessions/inspector_room_token/${booking_session_code}`,
      );

      if (data.status === 'success') {
        console.log('fetchInspectorRoomToken response data: ', data.data);
        return data.data;
      } else {
        throw new Error(
          'An error occurred while fetching inspector room token',
        );
      }
    },
    [axios],
  );

  const [documentContents, setDocumentContents] = useState<any>(null);

  const _fetchDocumentContents = async (bookingId: string) => {
    console.log(`::::call fetchDocumentContents for bookingId: ${bookingId}`);
    if (!bookingId) throw new Error('Error: missing bookingId.');

    const { data } = await axios.get(`sessions/document_contents/${bookingId}`);

    if (data.status === 'success') {
      console.log('session document contents: ', data.data);
      setDocumentContents(data.data);
      return data.data;
    } else {
      throw new Error(
        'An error occurred while fetching session document contents',
      );
    }
  };

  const {
    data: fetchedDocumentContents,
    isLoading: isFetchingDocumentContents,
    error: errorFetchingDocumentContents,
    refetch: fetchDocumentContents,
  } = useQuery(
    ['document-contents', bookingId],
    () => _fetchDocumentContents(bookingId),
    {
      retry: 0,
      refetchOnMount: false,
      enabled: false,
    },
  );

  const { mutateAsync: saveDocumentContents } = useMutation(
    async (payload: Record<string, any>) => {
      const { bookingId, documents } = payload;

      console.log('::::saveDocumentContents payload:', payload);

      const mutation = async () => {
        const { data } = await axios.post(
          `sessions/document_contents/${bookingId}`,
          {
            document_contents: documents,
          },
        );

        console.log('::::saveDocumentContents data:', data);
        if (data.status === 'success') {
          return { ...data.data, status: 'success' };
        } else {
          throw new Error('An error occurred while saving document contents');
        }
      };

      const response = await mutation();
      return response;
    },
  );

  const deleteDocumentContents = async (bookingId: string) => {
    await new Promise((resolve) => setTimeout(resolve, 500));

    const { data } = await axios.delete(
      `sessions/document_contents/${bookingId}`,
    );

    if (data.status === 'success') {
      return data.document_contents;
    } else {
      throw new Error('An error occurred while deleting document contents');
    }
  };

  const {
    mutate: resetDocumentSectionContents,
    data: resettedDocumentContents,
    isLoading: isResettingDocumentContents,
    error: errorResettingDocumentContents,
  } = useMutation(deleteDocumentContents);

  const getSessionLink = async (
    bookingSessionCode: string,
  ): Promise<string> => {
    console.log(`getSessionLink: ${bookingSessionCode}`);
    try {
      const { data } = await axios.get(
        `sessions/get_session_link/${bookingSessionCode}`,
      );

      if (data.status === 'success') {
        console.log('Session link data:', data);
        return data.sessionLink;
      }
    } catch (error) {
      console.error('Error fetching session link:', error);
    }
    return 'error';
  };

  const {
    mutateAsync: sendSessionLink,
    isLoading: isSendingSmsInvite,
    error: errorSendingSmsInvite,
  } = useMutation(
    async ({
      bookingSessionCode,
      type,
      alternativeNumber,
      timestamp,
    }: {
      bookingSessionCode: string;
      type: string;
      alternativeNumber?: string;
      timestamp?: number;
    }): Promise<ISmsSendStatus> => {
      console.log(
        `sendSessionLink: ${bookingSessionCode}, type: ${type}, value: ${alternativeNumber}`,
      );
      //await new Promise(resolve => setTimeout(resolve, 5000));

      try {
        const { data } = await axios.post(`sessions/send_session_link`, {
          booking_session_code: bookingSessionCode,
          type,
          alternativeNumber,
        });

        console.log('::::sendSessionLink data: ', data);
        return data as ISmsSendStatus;
      } catch (error) {
        console.log('::::sendSessionLink error: ', error);
        return {
          status: 'error',
          message: 'An error occurred while sending the link.',
        } as ISmsSendStatus;
      }
    },
  );

  const [mediaStatus, setMediaStatus] = useState<IMediaStatus[]>([]);
  const [pingMediaStatus, setPingMediaStatus] = useState<number>(0);
  const {
    data: _mediaStatus,
    isLoading: isFetchingMediaStatus,
    error: errorFetchingMediaStatus,
  } = useQuery<IMediaStatus[]>(
    ['media-status', bookingId],
    async () => {
      setPingMediaStatus((prev) => prev - 1);
      console.log(`Fetching media status for bookingId: ${bookingId}`);
      const { data } = await axios.get(`sessions/media_status/${bookingId}`);

      if (data.status === 'success') {
        console.log('Media status response data: ', data);
        setMediaStatus(data.media_status as IMediaStatus[]);
        return data.media_status as IMediaStatus[];
      } else {
        throw new Error('An error occurred while fetching media status');
      }
    },
    {
      enabled: !!bookingId && pingMediaStatus > 0,
      retry: 0,
      refetchInterval: 1000,
    },
  );

  const geocodePostcodeOSM = async (postcode: string) => {
    console.log('code', postcode);
    try {
      const response = await axios.get(
        'https://nominatim.openstreetmap.org/search',
        {
          params: {
            q: postcode,
            format: 'json',
            addressdetails: 1,
          },
        },
      );
      const results = response.data;
      if (results.length === 0) {
        throw new Error('No results found for the given postcode');
      }

      const { lat, lon } = results[0];
      console.log({ lat, lon });
      return { lat: parseFloat(lat), lon: parseFloat(lon) };
    } catch (error) {
      console.error('Error fetching geocode data:', error);
      throw error;
    }
  };

  const { mutateAsync: updateImage } = useMutation(
    async (payload: Record<string, any>) => {
      const { bookingId, image } = payload;
      const mutation = async () => {
        try {
          const { data } = await axios.post(
            `sessions/update_image/${bookingId}`,
            {
              image,
            },
          );

          if (data.status === 'success') {
            return data.data;
          }
        } catch (error) {
          console.log(`An error occurred while updating image: ${error}`);
        }
      };

      const response = await mutation();
      return response;
    },
  );

  const { mutateAsync: saveTranscribePart } = useMutation(
    async (payload: Record<string, any>) => {
      const { bookingId, part } = payload;
      const mutation = async () => {
        try {
          console.log('::::saveTranscribePart...', bookingId, part);
          const { data } = await axios.post(
            `sessions/save_transcribe_part/${bookingId}`,
            {
              part,
            },
          );

          return data.status === 'success';
        } catch (error) {
          console.log(`An error occurred while adding comment: ${error}`);
        }
      };

      const response = await mutation();
      return response;
    },
  );

  const getSipToken = useQuery(
    ['sip_token'],
    async () => {
      console.log(`getSipToken /sessions/sip_token`);
      const { data } = await axios.post(`/sessions/sip_token`);

      if (data.status === 'success') {
        console.log('sipToken data:', data.data);
        return data.data;
      }

      throw new Error('Failed to get sip token...');
    },
    {
      retry: 0,
      enabled: false,
    },
  );

  const { mutateAsync: sipDialout } = useMutation(
    async (payload: Record<string, any>) => {
      const mutation = async () => {
        try {
          console.log(`sipDialout /sessions/sip_dialout`);
          const { data } = await axios.post(`/sessions/sip_dialout`, payload);

          if (data.status === 'success') {
            console.log('sipDialout data:', data.data);
            return data.data;
          }

          throw new Error('Failed to get sip token...');
        } catch (error) {
          console.log(`An error occurred during SIP dialout: ${error}`);
        }
      };

      const response = await mutation();
      return response;
    },
  );

  const { mutateAsync: sipHangup } = useMutation(
    async (payload: Record<string, any>) => {
      const mutation = async () => {
        try {
          console.log(`sipHangup /sessions/sip_hangup`);
          const { data } = await axios.post(`/sessions/sip_hangup`, payload);

          console.log('sipHangup data:', data);
          if (data.status === 'success') {
            toggleToast(
              data.message || 'SIP call hung up successfully',
              'success',
            );
            return data.data;
          }

          throw new Error('Failed to hangup SIP call...');
        } catch (error) {
          console.log(`An error occurred during SIP hangup: ${error}`);
        }
      };

      const response = await mutation();
      return response;
    },
  );

  const exitVHC = useMutation(async (payload: Record<string, any>) => {
    const mutation = async () => {
      try {
        await axios.put(`sessions/exit_vhc`, {
          booking_id: payload.bookingId,
          session_chats: payload.sessionChats,
        });
      } catch (error) {
        console.log(`An error occurred while exiting VHC: ${error}`);
      }
    };

    const response = await mutation();
    return response;
  });

  const deleteImage = useMutation(async (payload: Record<string, any>) => {
    const { bookingId, imageId } = payload;
    const mutation = async () => {
      try {
        const { data } = await axios.delete(
          `sessions/delete_image/${bookingId}/${imageId}`,
        );

        if (data.status === 'success') {
          return data.data;
        }
      } catch (error) {
        console.log(`An error occurred while deleting image: ${error}`);
      }
    };

    const response = await mutation();
    return response;
  });

  const { mutateAsync: appendComment } = useMutation(
    async (payload: Record<string, any>) => {
      const { bookingId, comment } = payload;
      const mutation = async () => {
        try {
          const { data } = await axios.post(
            `sessions/add_comment/${bookingId}`,
            {
              comment,
            },
          );

          if (data.status === 'success') {
            return data.data;
          }
        } catch (error) {
          console.log(`An error occurred while adding comment: ${error}`);
        }
      };

      const response = await mutation();
      return response;
    },
  );

  const saveAttachment = async (bookingId: string, attachment: any) => {
    try {
      console.log('saveAttachment...', bookingId, attachment);

      const response = await axios.post(
        `/sessions/save_attachment/${bookingId}`,
        {
          bookingId,
          attachment,
        },
      );

      if (response.data.status === 'success') {
        return response.data;
      }
    } catch (error) {
      console.error('Error saving attachment:', error);
    }

    toggleToast('Error saving attachment.', 'error');
  };

  const resetTranscriptAttachments = async (payload: Record<string, any>) => {
    const { bookingId, attachments } = payload;

    try {
      const { data } = await axios.post(
        `/sessions/reset_attachments/${bookingId}`,
        {
          attachments,
        },
      );

      if (data.status === 'success') {
        return data;
      }
    } catch (error) {
      console.error('Error saving attachment:', error);
    }

    toggleToast('Error saving attachment.', 'error');
  };

  const { mutateAsync: uploadImage } = useMutation(
    async (payload: Record<string, any>) => {
      const { bookingId, formData } = payload;
      const mutation = async () => {
        try {
          console.log(
            `upload image for bookingId: ${bookingId} payload: `,
            payload,
          );

          const { data } = await axios.post(
            `sessions/upload_image/${bookingId}`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          );

          if (data.status === 'success' && !!data.image) {
            return data.image;
          }
        } catch (error) {
          console.log(`An error occurred while uploading image: ${error}`);
        }
      };

      return await mutation();
    },
  );

  const createSession = async (payload: Record<string, any>) => {
    const { bookingId, documentContents } = payload;

    try {
      const { data } = await axios.put(`/sessions/${bookingId}`, {
        document_contents: documentContents,
      });

      if (data.status === 'success') {
        return data;
      }
    } catch (error) {
      console.error('Error saving attachment:', error);
    }

    toggleToast('Error saving attachment.', 'error');
  };

  const deleteSession = async (bookingId: string) => {
    try {
      const { data } = await axios.delete(`/sessions/${bookingId}`);
      console.log('::::deleteSession data:', data);

      if (data.status !== 'success')
        throw new Error('Failed to delete session data');
      toggleToast('Session data deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting session data:', error);
      toggleToast('Failed to delete session data', 'error');
    }
  };

  const restoreSession = async ({ formData }: { formData: FormData }) => {
    try {
      console.log('::::restoreSession formData:', formData);

      const { data } = await axios.post(`/sessions/restore`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('::::restoreSession data:', data);

      if (data.status === 'success') {
        return toggleToast(
          data.message || 'Session data restored successfully',
          'success',
        );
      }

      throw new Error('Failed to restore session data');
    } catch (error) {
      console.error('Error restoring session:', error);
      toggleToast('Failed to restore session', 'error');
    }
  };

  return {
    sessionData,
    isFetchingSessionDetails,
    errorFetchingSessionDetails,
    refetchSessionDetails,

    documentContents,
    fetchDocumentContents,
    isFetchingDocumentContents,
    errorFetchingDocumentContents,
    saveDocumentContents,

    resettedDocumentContents,
    isResettingDocumentContents,
    errorResettingDocumentContents,
    resetDocumentSectionContents,

    downloadSessionData,

    fetchInspectorRoomToken,
    getSessionLink,
    sendSessionLink,
    isSendingSmsInvite,
    errorSendingSmsInvite,

    updateImage,
    getSipToken,

    sipDialout,
    sipHangup,

    geocodePostcodeOSM,
    exitVHC,
    deleteImage,
    appendComment,
    saveTranscribePart,

    mediaStatus,
    setMediaStatus,
    isFetchingMediaStatus,
    errorFetchingMediaStatus,
    pingMediaStatus,
    setPingMediaStatus,
    saveAttachment,
    resetTranscriptAttachments,

    uploadImage,
    createSession,
    deleteSession,
    restoreSession,
  };
};

export default useSession;
